import React, { useState, useEffect, useRef, useMemo } from "react";
import { 
  Box,
  Button,
  CircularProgress
} from "@mui/material";
import { DataGrid, GridColDef, GridOverlay } from "@mui/x-data-grid";
import "./MdmReportsPreview.css";
import { newReportConfig, MergedFilterSource } from "./mdmReportsUtils";
import { fetchAvailableFilters, fetchReportData, fetchColumnDefinitions, FilterOption, ColumnOption } from "../../services/reportsDataService";
import { isMergedFilterForReport, getMergedFilterSources } from "../../services/mdmCustomFiltersService";
import dayjs from "dayjs";
import { openPopup } from "../../stateManagement/actions/popupActions";
import CompactCheckboxDropdown from "./components/CompactCheckboxDropdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines } from "@fortawesome/free-solid-svg-icons";
import { buildLocationFilters, buildUserFilters } from "../../services/mdmReportsDownloadService";

interface DropdownOptionsMap {
  [key: string]: { label: string; value: string }[];
}

interface LoadingMap {
  [key: string]: boolean;
}

interface MdmReportsPreviewProps {
  onBack?: () => void;
  newReportConfig: newReportConfig | null;
  filters: Record<string, string[]>;
  customFiltersLoading?: boolean;
  dateRange: {
    type: string;
    fromDate: string;
    toDate: string;
    selectedRange: string;
    period?: string;
    year?: string;
  };
  salesDrillDownPath?: Array<{ level: string; value: string }>;
  geoDrillDownPath?: Array<{ level: string; value: string }>;
  optionsMap?: DropdownOptionsMap;
  loadingMap?: LoadingMap;
  onFilterChange?: (key: string, value: string | null) => void;
  onMultiFilterChange?: (key: string, values: string[]) => void;
  onFilterOpen?: (key: string) => void;
  onFilterInputChange?: (key: string, inputValue: string) => void;
  onLoadDistributorOptions?: (fieldKey: string) => void;
  searchTextMap?: Record<string, string>;
  shouldDisableDistributorFilter?: boolean;
  distributorDisabledReason?: string;
  customFilters?: FilterOption[];
  showPreview?: boolean;
  mergedFilters?: Record<string, MergedFilterSource[]>;
}


const MdmReportsPreview: React.FC<MdmReportsPreviewProps> = ({ 
  onBack, 
  newReportConfig, 
  filters, 
  dateRange,
  salesDrillDownPath = [],
  geoDrillDownPath = [],
  optionsMap = {},
  loadingMap = {},
  onFilterChange,
  onMultiFilterChange,
  onFilterOpen,
  onFilterInputChange,
  onLoadDistributorOptions,
  searchTextMap = {},
  shouldDisableDistributorFilter = false,
  distributorDisabledReason,
  customFilters = [],
  showPreview = false,
  mergedFilters = {},
  customFiltersLoading = false
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [reportData, setReportData] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [columnDefinitions, setColumnDefinitions] = useState<ColumnOption[]>([]);
  const [loading, setLoading] = useState(false);
  const selectedReport = newReportConfig ?? null;
  const pageSize = 30;
  const ROW_HEIGHT = 36;
  const GRID_HEADER_HEIGHT = 56;
  const GRID_HEIGHT = ROW_HEIGHT * pageSize + GRID_HEADER_HEIGHT;
  const lastRequestIdRef = useRef(0);
  const pageRowCountsRef = useRef<Record<number, number>>({});
  const totalRowCountRef = useRef(0);
  const lastReportNameRef = useRef<string | null>(null);
  const isFirstFetchAfterReportChangeRef = useRef(false);

  const filtersKey = useMemo(() => {
    const levelKeys = [
      newReportConfig?.salesHierarchyFilter?.levelFilterField,
      newReportConfig?.geographicalHierarchyFilter?.levelFilterField
    ].filter(Boolean) as string[];
    
    const salesHierarchyOrder = newReportConfig?.salesHierarchyFilter?.hierarchyOrder || [];
    const supplierLevel = salesHierarchyOrder.length > 0 ? salesHierarchyOrder[salesHierarchyOrder.length - 1] : 'supplier';
  const hierarchyLevelsToExclude = new Set<string>();
  
  if (newReportConfig?.salesHierarchyFilter?.hierarchyOrder) {
    newReportConfig.salesHierarchyFilter.hierarchyOrder.forEach(level => {
      if (level !== supplierLevel) {
        hierarchyLevelsToExclude.add(level);
      }
    });
  }

  if (newReportConfig?.geographicalHierarchyFilter?.hierarchyOrder) {
    newReportConfig.geographicalHierarchyFilter.hierarchyOrder.forEach(level => {
      hierarchyLevelsToExclude.add(level);
    });
  }
    const relevantFilters: Record<string, string[]> = {};
    Object.entries(filters).forEach(([key, values]) => {
      if (levelKeys.includes(key)) {
        return;
      }
      if (hierarchyLevelsToExclude.has(key)) {
        return;
      }
      
      if (values && values.length > 0 && values[0] !== "") {
        relevantFilters[key] = values;
      }
    });
    
    if (salesDrillDownPath && salesDrillDownPath.length > 0) {
      salesDrillDownPath.forEach(item => {
        if (!relevantFilters[item.level]) {
          relevantFilters[item.level] = [];
        }
        if (!relevantFilters[item.level].includes(item.value)) {
          relevantFilters[item.level].push(item.value);
        }
      });
    }
    
    if (geoDrillDownPath && geoDrillDownPath.length > 0) {
      geoDrillDownPath.forEach(item => {
        if (!relevantFilters[item.level]) {
          relevantFilters[item.level] = [];
        }
        if (!relevantFilters[item.level].includes(item.value)) {
          relevantFilters[item.level].push(item.value);
        }
      });
    }
    
    return JSON.stringify(relevantFilters);
  }, [filters, newReportConfig, geoDrillDownPath, salesDrillDownPath]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 300);   
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
    pageRowCountsRef.current = {}; 
  }, [debouncedSearch]);

  useEffect(() => {
    setCurrentPage(1);
    pageRowCountsRef.current = {}; 
  }, [newReportConfig, filtersKey]);

  useEffect(() => {
    setCurrentPage(1);
    pageRowCountsRef.current = {}; 
  }, [dateRange]);

  useEffect(() => {
    if (newReportConfig && newReportConfig.isLiveReport !== true) {
      fetchData();
    }
  }, [newReportConfig, filtersKey, debouncedSearch, currentPage, dateRange, salesDrillDownPath, geoDrillDownPath]);


   useEffect(() => {
    const loadReportMetadata = async () => {
      if (!selectedReport?.reportName) {
        setColumnDefinitions([]);
        return;
      }
      
      try {
        const columns = await fetchColumnDefinitions(selectedReport.reportName);
        setColumnDefinitions(columns);
      } catch (error) {
        setColumnDefinitions([]);
      }
    };
    
    loadReportMetadata();
  }, [newReportConfig?.reportName]);

  const fetchData = async () => {
    if (!newReportConfig?.reportName) return;

    const requestId = ++lastRequestIdRef.current;

    const reportChanged = lastReportNameRef.current !== null && 
                          lastReportNameRef.current !== newReportConfig.reportName;
    const shouldSuppressPopup = reportChanged && currentPage === 1;

    if (lastReportNameRef.current !== newReportConfig.reportName) {
      if (lastReportNameRef.current !== null) {
        isFirstFetchAfterReportChangeRef.current = true;
      }
      lastReportNameRef.current = newReportConfig.reportName;
    }
    
    const apiFilters: Record<string, string[]> = {};
    const levelKeys = [
      newReportConfig.salesHierarchyFilter?.levelFilterField,
      newReportConfig.geographicalHierarchyFilter?.levelFilterField
    ].filter(Boolean) as string[];
    
    const hierarchyOrder = newReportConfig.salesHierarchyFilter?.hierarchyOrder || [];
    const supplierLevel = hierarchyOrder[hierarchyOrder.length - 1] || 'supplier';
    
    const distributorFilterField = newReportConfig.distributorFilter?.field || 'distributor_code';

    const authContext = JSON.parse(localStorage.authContext || '{}');
    const loggedInUser = authContext?.user?.loginId;

    // Handle distributor view for logged-in user
    if(newReportConfig.isDistributorView && loggedInUser){
      apiFilters.distributor_code = [loggedInUser];
    }
    
    const locationFilters = buildLocationFilters(geoDrillDownPath);
    const userFilters = buildUserFilters(
      salesDrillDownPath,
      newReportConfig.salesHierarchyFilter?.hierarchyOrder || []
    );

    let distributorCodesFound = false;
    
    if (filters[distributorFilterField] && filters[distributorFilterField].length > 0) {
      apiFilters.distributor_code = filters[distributorFilterField];
      distributorCodesFound = true;
    }
    
    if (!distributorCodesFound && filters[supplierLevel] && filters[supplierLevel].length > 0) {
      apiFilters.distributor_code = filters[supplierLevel];
      distributorCodesFound = true;
    }
    
      Object.entries(filters).forEach(([key, values]) => {
        if (levelKeys.includes(key)) {
          return;
        }
        
        if (key === supplierLevel || key === distributorFilterField) {
          return;
        }
        
        if (isMergedFilterForReport(key, newReportConfig)) {
          return;
        }
        
        if (key.endsWith('_dynamic')) {
          const mergedFilterAlias = key.replace('_dynamic', '');
          if (isMergedFilterForReport(mergedFilterAlias, newReportConfig)) {
            const mergedFilterSelections = filters[mergedFilterAlias] || [];
            if (mergedFilterSelections.length > 0) {
              const selectedValue = mergedFilterSelections[0];
              const mergedSources = getMergedFilterSources(mergedFilterAlias, newReportConfig);
              const source = mergedSources?.find(s => s.value === selectedValue);
              const sourceAlias = source?.alias;
              if (sourceAlias && values && values.length > 0 && values[0] !== "") {
                apiFilters[sourceAlias] = values;
              }
            }
            return;
          }
        }
        
        if(values && values.length > 0 && values[0] !== ""){
          apiFilters[key] = values;
        }
      });
      const hasDrillDownPaths = (locationFilters.length > 0 || userFilters.length > 0);
const hasDistributorCodes = apiFilters.distributor_code && apiFilters.distributor_code.length > 0;
    if (!hasDrillDownPaths && !hasDistributorCodes) {
      if (requestId === lastRequestIdRef.current) {
        setReportData([]);
        setHasMore(false);
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    
    try {
      const commaSeparatedFilters = Array.from(
        new Set([
          'distributor_code',
          ...customFilters.map(filter => filter.alias)
        ])
      );


      const apiFiltersString = JSON.stringify(apiFilters || {});
    const apiFiltersEncodedSize = encodeURIComponent(apiFiltersString).length;

    // if (commaSeparatedFilters.length > MAX_COMMA_SEP_FILTERS || apiFiltersEncodedSize > MAX_API_FILTER_BYTES) {
    //   // Only suppress popup on first fetch after report change (to avoid stale popup from previous report)
    //   if (!shouldSuppressPopup) {
    //     openPopup(
    //       "Error",
    //       "You've selected a large number of data. Report preview is disabled for this selection, but you can still download the full report."
    //     );
    //   }
    //   if (requestId === lastRequestIdRef.current) {
    //     setReportData([]);
    //     setHasMore(false);
    //     // Reset the flag after handling the first fetch
    //     if (isFirstFetchAfterReportChangeRef.current) {
    //       isFirstFetchAfterReportChangeRef.current = false;
    //     }
    //   }
    //   return;
    // }
    
    // Reset the flag after successful fetch
    if (isFirstFetchAfterReportChangeRef.current) {
      isFirstFetchAfterReportChangeRef.current = false;
    }
      const distributorFilterPayload =
        (locationFilters.length > 0 || userFilters.length > 0)
          ? {
              locationFilters: locationFilters.length > 0 ? locationFilters : undefined,
              userFilters: userFilters.length > 0 ? userFilters : undefined
            }
          : undefined;

      const requestParams: any = {
        getAPI: newReportConfig.getAPI,
        report: newReportConfig.reportName,
        page: currentPage,
        pageSize: pageSize,
        contains: debouncedSearch || undefined,
        filters: Object.keys(apiFilters).length > 0 ? apiFilters : undefined,
        commaSeparatedFilters,
        distributorFilter: distributorFilterPayload
      };

      if (dateRange.type === "period" && dateRange.period && dateRange.year) {
        requestParams.period = dateRange.period;
        requestParams.year = dateRange.year;
      } else if (dateRange.fromDate && dateRange.toDate) {
        const fromDateDayjs = dayjs(dateRange.fromDate, "DD-MM-YYYY");
        const toDateDayjs = dayjs(dateRange.toDate, "DD-MM-YYYY");
        
        const fromDateISO = new Date(Date.UTC(
          fromDateDayjs.year(),
          fromDateDayjs.month(),
          fromDateDayjs.date() - 1,
          18,
          30,
          0
        )).toISOString().replace(/\.\d{3}Z$/, 'Z');
        
        const toDateISO = new Date(Date.UTC(
          toDateDayjs.year(),
          toDateDayjs.month(),
          toDateDayjs.date(),
          18,
          29,
          59
        )).toISOString().replace(/\.\d{3}Z$/, 'Z');
        if(newReportConfig.dateRangeFilter){
          requestParams.since = fromDateISO;
          requestParams.until = toDateISO;
        }
      }
      
      const response = await fetchReportData(requestParams);

      if (requestId === lastRequestIdRef.current) {
        const items = response.items || [];
        setReportData(items);
        setHasMore(response.total > (currentPage * pageSize) && currentPage < response.totalPages);
        pageRowCountsRef.current[currentPage] = items.length;
        totalRowCountRef.current = response.total;
      }
    } catch (error) {
      if (requestId === lastRequestIdRef.current) {
        setReportData([]);
        setHasMore(false);
        pageRowCountsRef.current[currentPage] = 0; 
        totalRowCountRef.current = 0;
      }
    } finally {
      if (requestId === lastRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const generateColumnsFromData = (data: any[]): GridColDef[] => {
    if (!data || data.length === 0) {
      return [];
    }
    const columnsToHide = newReportConfig?.columnsToHide || [];
    
    const dataKeys = Object.keys(data[0]).filter(k => k !== '@filters' && !columnsToHide.includes(k));
    
    if (columnDefinitions.length > 0) {
      const orderedColumns = columnDefinitions
        .filter(colDef => dataKeys.includes(colDef.alias))
        .map(colDef => ({
          field: colDef.alias,
          headerName: colDef.display,
          minWidth: 150,
          flex: 1,
          sortable: false,
        }));
      return orderedColumns;
    }
    return [];
  };

  const rowsToShow = reportData || [];

  const effectiveColumns: GridColDef[] = useMemo(() => {
    return generateColumnsFromData(rowsToShow);
  }, [rowsToShow, columnDefinitions, newReportConfig?.columnsToHide]);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (hasMore) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const getDisplayRange = useMemo(() => {
      let rowCount: number;
      
      if (loading) {
        rowCount = pageRowCountsRef.current[currentPage];
        
        if (rowCount === undefined) {
          rowCount = pageSize;
        }
      } else {
        rowCount = rowsToShow.length;
        if (rowCount > 0) {
          pageRowCountsRef.current[currentPage] = rowCount;
        }
      }
      
      if (rowCount === 0) {
        return { start: 0, end: 0, total: '0' };
      }
      
      const start = (currentPage - 1) * pageSize + 1;
      const end = (currentPage - 1) * pageSize + rowCount;
      const total = hasMore ? totalRowCountRef.current : end.toString();
      return { start, end, total };
    }, [currentPage, rowsToShow.length, loading, hasMore, pageSize]);
  
    const { start, end, total } = getDisplayRange;

  const customNoRowsOverlay = () => (
    <GridOverlay>
      <div>No records found</div>
    </GridOverlay>
  );

  const showCustomFilters = Boolean(
    (newReportConfig?.shouldShowCustomFilters ?? true) && customFilters.length > 0
  );

  let text = "Select filters to generate report";
  if(newReportConfig?.isLiveReport){
    text = "Live reports cannot be previewed. Kindly download the report to continue.";
  }
  if(newReportConfig?.isPDFReport){
    text = "PDF reports cannot be previewed. Kindly download the report to continue.";
  }

  return (
    <div className="mdm-preview-container">
      {/* Custom Filters Section - Always Visible */}
      {showCustomFilters && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          px: 1, 
          py: 1,
          borderBottom: '1px solid #e5e5e5'
        }}>
          {customFiltersLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <span style={{ fontSize: 13, color: '#666' }}>Loading filters...</span>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1.5, flex: 1, flexWrap: 'wrap' }}>
              {customFilters.map((filter) => {
                const filterKey = filter.alias;
                const options = optionsMap[filterKey] || [];
                const selectedValues = filters[filterKey] || [];
                const isLoading = loadingMap[filterKey] || false;
                const searchText = searchTextMap[filterKey] || "";
                return (
                  <div key={filterKey}>
                    <CompactCheckboxDropdown
                      label={filter.display}
                      options={options}
                      selectedValues={selectedValues}
                      onChange={(values) => onMultiFilterChange?.(filterKey, values)}
                      onOpen={() => onFilterOpen?.(filterKey)}
                      loading={isLoading}
                      placeholder={filter.display}
                      width={152}
                      enableSelectAll={true}
                      dropdownWidth={250}
                      searchText={searchText}
                      onSearchChange={
                        onFilterInputChange ? (value) => onFilterInputChange(filterKey, value) : undefined
                      }
                    />
                  </div>
                );
              })}
              {/* Merged Filters (hardcoded options) */}
              {Object.keys(mergedFilters || {}).map((mergedKey) => {
                const options = optionsMap[mergedKey] || [];
                const selectedValues = filters[mergedKey] || [];
                const isLoading = loadingMap[mergedKey] || false;
                const searchText = searchTextMap[mergedKey] || "";
                
                // Get selected value and its corresponding source alias
                const selectedValue = selectedValues.length > 0 ? selectedValues[0] : null;
                const mergedSources = getMergedFilterSources(mergedKey, newReportConfig);
                const source = selectedValue ? mergedSources?.find(s => s.value === selectedValue) : null;
                const selectedSourceAlias = source?.alias;
                // Dynamic dropdown key and state
                const dynamicKey = `${mergedKey}_dynamic`;
                const dynamicOptions = selectedSourceAlias ? (optionsMap[selectedSourceAlias] || []) : [];
                const dynamicSelectedValues = filters[dynamicKey] || [];
                const dynamicIsLoading = loadingMap[selectedSourceAlias || ''] || false;
                const dynamicSearchText = searchTextMap[dynamicKey] || "";
                
                return (
                  <Box key={mergedKey} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CompactCheckboxDropdown
                      label={mergedKey}
                      options={options}
                      selectedValues={selectedValues}
                      onChange={(values) => onMultiFilterChange?.(mergedKey, values)}
                      onOpen={() => onFilterOpen?.(mergedKey)}
                      loading={isLoading}
                      placeholder={mergedKey}
                      width={152}
                      enableSelectAll={false}
                      multiSelect={false}
                      dropdownWidth={250}
                      searchText={searchText}
                      onSearchChange={
                        onFilterInputChange ? (value) => onFilterInputChange(mergedKey, value) : undefined
                      }
                    />
                    {/* Dynamic dropdown - shown when a merged filter value is selected */}
                    {selectedValue && selectedSourceAlias && (
                      <CompactCheckboxDropdown
                        label={selectedValue}
                        options={dynamicOptions}
                        selectedValues={dynamicSelectedValues}
                        onChange={(values) => onMultiFilterChange?.(dynamicKey, values)}
                        onOpen={() => {
                          if (onFilterOpen && selectedSourceAlias) {
                            onFilterOpen(selectedSourceAlias);
                          }
                        }}
                        loading={dynamicIsLoading}
                        placeholder={selectedSourceAlias}
                        width={152}
                        enableSelectAll={true}
                        multiSelect={true}
                        dropdownWidth={250}
                        searchText={dynamicSearchText}
                        onSearchChange={
                          onFilterInputChange ? (value) => onFilterInputChange(dynamicKey, value) : undefined
                        }
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* DataGrid Container - Only visible when showPreview is true */}
      {showPreview ? (
        <>
          <Box 
            className="mdm-preview-table-container" 
            sx={{ 
              height: `${GRID_HEIGHT}px`,
              width: '100%',
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top left',
              '& .MuiDataGrid-root': {
                border: 'none',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#F3F9FB',
                fontWeight: 600,
                fontSize: '13px'
              },
              '& .MuiDataGrid-cell': {
                fontSize: '12px',
                borderRight: '1px solid #e0e0e0',
                minHeight: '35px !important',
                maxHeight: '35px !important'
              },
              '& .MuiDataGrid-row:nth-of-type(even)': {
                backgroundColor: '#f9f9f9'
              }
            }}
          >
            <DataGrid
              rows={rowsToShow}
              columns={effectiveColumns}
              getRowId={(row) => row.id || row._id || Math.random().toString()}
              pageSizeOptions={[pageSize]}
              rowHeight={ROW_HEIGHT}
              columnHeaderHeight={GRID_HEADER_HEIGHT}
              disableRowSelectionOnClick
              disableColumnMenu
              hideFooter
              loading={loading}
              components={{
                NoRowsOverlay: customNoRowsOverlay
              }}
              sx={{
                '& .MuiDataGrid-virtualScroller': {
                  minHeight: '400px'
                },
                '& .MuiDataGrid-cell': {
                  minHeight: '35px !important',
                  maxHeight: '35px !important'
                },
                '& .MuiDataGrid-row': {
                  minHeight: '35px !important',
                  maxHeight: '35px !important'
                }
              }}
            />
          </Box>

          {/* Sticky Pagination Footer */}
          <Box 
            className="mdm-preview-pagination-footer"
            sx={{
              position: 'sticky',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: '#ffffff',
              borderTop: '1px solid #e5e5e5',
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              zIndex: 1000,
              boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.04)'
            }}
          >
            {/* Left side - Display range */}
            {loading ? (
              <CircularProgress size={16} sx={{ color: '#1C7EA0' }} />
            ) : (
              <Box sx={{ fontSize: '14px', color: '#4a4a4a', fontWeight: 500 }}>
                {start}-{end} of {total}
              </Box>
            )}

            {/* Right side - Pagination controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Button
                size="small"
                onClick={handlePreviousPage}
                disabled={currentPage === 1 || loading}
                sx={{
                  color: currentPage === 1 ? '#cccccc' : '#666666',
                  padding: '6px 12px',
                  fontSize: '14px',
                  textTransform: 'none',
                  minWidth: 'auto',
                  '&:hover': {
                    backgroundColor: currentPage === 1 ? 'transparent' : '#f5f5f5'
                  },
                  '&.Mui-disabled': {
                    color: '#cccccc'
                  }
                }}
              >
                Prev
              </Button>

              {/* Page number display (non-clickable rounded icon) */}
              <Box
                sx={{
                  minWidth: '32px',
                  height: '32px',
                  padding: 0,
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#ffffff',
                  backgroundColor: '#1C7EA0',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'default'
                }}
              >
                {currentPage}
              </Box>

              <Button
                size="small"
                onClick={handleNextPage}
                disabled={!hasMore || loading}
                sx={{
                  color: !hasMore ? '#cccccc' : '#666666',
                  padding: '6px 12px',
                  fontSize: '14px',
                  textTransform: 'none',
                  minWidth: 'auto',
                  '&:hover': {
                    backgroundColor: !hasMore ? 'transparent' : '#f5f5f5'
                  },
                  '&.Mui-disabled': {
                    color: '#cccccc'
                  }
                }}
              >
                Next
              </Button>
            </Box>
          </Box>
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            minHeight: '400px',
            textAlign: 'center'
          }}
        >
          <FontAwesomeIcon icon={faFileLines} style={{ color: '#bbb', fontSize: 35 }} />
          <h5 style={{ color: "#888", fontSize: "15px", marginTop: '16px', margin: '16px 0 0 0' }}>{text}</h5>
        </Box>
      )}
    </div>
  );
};

export default MdmReportsPreview;